import { cva, type VariantProps } from 'class-variance-authority';
import { MdCheckCircle, MdInfo, MdWarning, MdWorkspacePremium } from 'react-icons/md';
import type { HTMLAttributes, ReactNode } from 'react';

import { cn } from '../lib/cn';

const alertVariants = cva('flex w-full items-start gap-3 rounded-field border p-3 text-sm', {
  variants: {
    tone: {
      danger: 'border-danger/25 bg-danger-soft text-danger',
      warning: 'border-warning/25 bg-warning-soft text-warning',
      success: 'border-success/25 bg-success-soft text-success',
      info: 'border-brand-600/20 bg-brand-50 text-brand-700',
      /** Formule Gold uniquement : distinct de `info`, réservé à ce seul usage. */
      gold: 'border-gold-500/40 bg-gold-100 text-gold-900',
    },
  },
  defaultVariants: { tone: 'info' },
});

const icons = {
  danger: MdWarning,
  warning: MdWarning,
  success: MdCheckCircle,
  info: MdInfo,
  gold: MdWorkspacePremium,
} as const;

export type AlertProps = HTMLAttributes<HTMLDivElement> &
  VariantProps<typeof alertVariants> & {
    /** Titre en gras au-dessus du message. */
    title?: ReactNode;
  };

/** Bandeau d'état de formulaire (erreur de connexion, confirmation…). */
export function Alert({ className, tone = 'info', title, children, ...props }: AlertProps) {
  const Icon = icons[tone ?? 'info'];

  return (
    <div
      role={tone === 'danger' ? 'alert' : 'status'}
      className={cn(alertVariants({ tone }), className)}
      {...props}
    >
      <Icon className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
      <div className="flex-1">
        {title ? <p className="font-semibold">{title}</p> : null}
        {children ? <p className={cn(title && 'mt-0.5 opacity-90')}>{children}</p> : null}
      </div>
    </div>
  );
}

export { alertVariants };
